import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsersTable } from '@modules/users/hooks/useUsersTable';
import { userGroupsService } from '@modules/userGroups/services/userGroupsService';

export const useAddUsersByGroupModal = ({ onClose, groupId }) => {
    const { t } = useTranslation();
    const {
        data,
        loading,
        error,
        getData,
        pageNumber,
        pageSize,
        sortDir,
        searchQuery,
        searchType,
        searchOptions,
        handleSort,
        setSearchQuery,
        setSearchType,
        setPageSize,
        setpageNumber,
        handleSearchClick,
        fetchData,
        userService
    } = useUsersTable();

    const [selectedUsers, setSelectedUsers] = useState([]);
    const [saving, setSaving] = useState(false);
    const [existingUsers, setExistingUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const modalRef = useRef(null);

    useEffect(() => {
        const fetchAllUsers = async () => {
            try {
                const response = await userService.getUsers({
                    pageNumber: 0,
                    pageSize: 1000, 
                    sortBy: 'username',
                    sortDir
                });
                setAllUsers(response.content);
            } catch (error) {
                console.error('Error fetching all users:', error);
            }
        };
        fetchAllUsers();    
    }, [userService, sortDir]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    useEffect(() => {
        const fetchExistingUsers = async () => {
            try {
                const users = await userGroupsService.getUsersByGroup(groupId);
                setExistingUsers(users.map(user => user.id));
            } catch (error) {
                console.error('Error fetching existing users:', error);
            }
        };
        fetchExistingUsers();
    }, [groupId]);

    const allFilteredData = allUsers.filter(user => !existingUsers.includes(user.id));
    
    const startIndex = pageNumber * pageSize;
    const endIndex = Math.min(startIndex + pageSize, allFilteredData.length);
    const filteredData = allFilteredData.slice(startIndex, endIndex);

    const handleCheckboxChange = (userId) => {
        setSelectedUsers(prev => {
            const newSelection = prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId];
            return newSelection;
        });
    };

    const handleSave = async () => {
        if (selectedUsers.length === 0) return;
        
        try {
            setSaving(true);
            await userGroupsService.attachUsers(groupId, selectedUsers);
            onClose('closeAll');
        } catch (error) {
            console.error('Error attaching users:', error);
        } finally {
            setSaving(false);
        }
    };

    return {
        // Refs
        modalRef,

        // States
        selectedUsers,
        saving,
        filteredData,
        loading,
        error,

        // Table props
        pageNumber,
        pageSize,
        sortDir,
        totalElements: allFilteredData.length,
        totalPages: Math.ceil(allFilteredData.length / pageSize),
        searchQuery,
        searchType,
        searchOptions,

        // Handlers
        handleCheckboxChange,
        handleSave,
        handleSort,
        setSearchQuery,
        setSearchType,
        setPageSize,
        setpageNumber,
        handleSearchClick,
        getData,

        // Utils
        t
    };
}; 